import { useCallback, useRef, useState } from "react";
import { Upload, Trash2, GripVertical } from "lucide-react";
import { api, formatApiError } from "../context/AuthContext";
import { toast } from "sonner";

// Turn a stored URL like "/api/files/..." into an absolute URL for <img>.
const resolveUrl = (u) => {
  if (!u) return "";
  if (u.startsWith("http://") || u.startsWith("https://")) return u;
  return `${process.env.REACT_APP_BACKEND_URL}${u}`;
};

const PhotoDropzone = ({ photos, onChange, cover, onCoverChange, max = 10 }) => {
  const [uploading, setUploading] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const [dragIndex, setDragIndex] = useState(null);
  const inputRef = useRef(null);

  const remaining = Math.max(0, max - photos.length);

  const uploadFiles = useCallback(async (files) => {
    const list = Array.from(files).filter((f) => f.type.startsWith("image/"));
    if (!list.length) return;
    if (list.length > remaining) {
      toast.error(`Limite de ${max} fotografias.`);
    }
    const toUpload = list.slice(0, remaining);
    if (!toUpload.length) return;
    setUploading(true);
    try {
      const uploaded = [];
      for (const file of toUpload) {
        const fd = new FormData();
        fd.append("file", file);
        const { data } = await api.post("/admin/upload", fd, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        uploaded.push(data.url);
      }
      onChange([...photos, ...uploaded]);
      toast.success(`${uploaded.length} foto${uploaded.length === 1 ? "" : "s"} carregada${uploaded.length === 1 ? "" : "s"}.`);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Falha ao carregar.");
    } finally {
      setUploading(false);
    }
  }, [photos, onChange, remaining, max]);

  const onDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    if (e.dataTransfer?.files?.length) {
      uploadFiles(e.dataTransfer.files);
    }
  };
  const onBrowse = (e) => {
    if (e.target.files?.length) uploadFiles(e.target.files);
    e.target.value = "";
  };

  const setAsCover = (url) => onCoverChange(url);
  const removePhoto = (i) => {
    const next = photos.filter((_, idx) => idx !== i);
    onChange(next);
    if (cover === photos[i]) onCoverChange(next[0] || "");
  };

  // drag-reorder
  const startDrag = (i) => setDragIndex(i);
  const dropReorder = (i) => {
    if (dragIndex === null || dragIndex === i) return;
    const copy = [...photos];
    const [moved] = copy.splice(dragIndex, 1);
    copy.splice(i, 0, moved);
    setDragIndex(null);
    onChange(copy);
  };

  return (
    <div>
      <div
        onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
        onDragLeave={() => setDragOver(false)}
        onDrop={onDrop}
        onClick={() => inputRef.current?.click()}
        className={`border-2 border-dashed ${dragOver ? "border-[#C5A059] bg-[#C5A059]/5" : "border-[#0A1128]/20 bg-[#F4F1EB]/40"} p-8 text-center cursor-pointer transition-colors`}
        data-testid="photo-dropzone"
      >
        <input
          ref={inputRef}
          type="file"
          multiple
          accept="image/*"
          className="hidden"
          onChange={onBrowse}
          data-testid="photo-dropzone-input"
        />
        <Upload size={28} strokeWidth={1.3} className="mx-auto text-[#C5A059]" />
        <p className="mt-3 text-sm text-[#0A1128] font-medium tracking-wide">
          {uploading ? "A carregar…" : "Arraste imagens para aqui ou clique para escolher"}
        </p>
        <p className="mt-1 text-xs text-[#3A435E]">
          Até {max} fotografias · JPG, PNG, WEBP, HEIC · máx 12MB cada · {photos.length}/{max}
        </p>
      </div>

      {photos.length > 0 && (
        <div className="mt-5 grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="photo-grid">
          {photos.map((p, i) => {
            const isCover = cover === p;
            return (
              <div
                key={p + i}
                draggable
                onDragStart={() => startDrag(i)}
                onDragOver={(e) => e.preventDefault()}
                onDrop={() => dropReorder(i)}
                className={`group relative border ${isCover ? "border-[#C5A059]" : "border-[#0A1128]/15"} bg-[#FCFAF8] overflow-hidden`}
                data-testid={`photo-tile-${i}`}
              >
                <div className="aspect-[4/3] relative">
                  <img src={resolveUrl(p)} alt={`foto ${i + 1}`} className="w-full h-full object-cover" />
                  <div className="absolute inset-0 bg-[#0A1128]/0 group-hover:bg-[#0A1128]/40 transition-colors" />
                  <div className="absolute top-2 left-2 bg-[#0A1128]/70 text-[#F4F1EB] text-[10px] tracking-[0.2em] uppercase px-2 py-1 flex items-center gap-1">
                    <GripVertical size={10} /> {String(i + 1).padStart(2, "0")}
                  </div>
                  {isCover && (
                    <div className="absolute top-2 right-2 bg-[#C5A059] text-[#0A1128] text-[10px] tracking-[0.2em] uppercase px-2 py-1">
                      Capa
                    </div>
                  )}
                  <div className="absolute bottom-2 inset-x-2 flex gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
                    {!isCover && (
                      <button
                        type="button"
                        onClick={(e) => { e.stopPropagation(); setAsCover(p); }}
                        className="flex-1 bg-[#FCFAF8]/95 text-[#0A1128] text-[10px] tracking-[0.2em] uppercase py-1.5"
                        data-testid={`photo-set-cover-${i}`}
                      >
                        Definir capa
                      </button>
                    )}
                    <button
                      type="button"
                      onClick={(e) => { e.stopPropagation(); removePhoto(i); }}
                      className="bg-[#9B2226]/95 text-white px-3 py-1.5"
                      data-testid={`photo-remove-${i}`}
                    >
                      <Trash2 size={12} />
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default PhotoDropzone;
export { resolveUrl };
